import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type {
  OnlinePaymentProvider,
  PaymentProviderConfig,
  PaymentProviderMode,
} from '@restaurant/api-contracts';
import {
  CARD_ON_HANDOFF_METHOD_ID,
  CASH_METHOD_ID,
  ONLINE_PROVIDER_IDENTITY,
  ONLINE_PROVIDER_METHOD_ID_BY_ENUM,
  PAYMENT_METHOD_NOTE_MAX_LENGTH,
} from '@restaurant/types';
import { useErrorT, useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { resolveErrorText } from '@/api/errors';
import { usePaymentProviders, useSetProviderActivation } from '../hooks/usePaymentProviders';
import { usePaymentMethodNotes, useSetPaymentMethodNote } from '../hooks/usePaymentMethodNotes';
import {
  useManualPaymentMethods,
  useDeleteManualPaymentMethod,
  useOrderTypeConfig,
  useUpdateOrderTypeConfig,
} from '@/features/order-setup/hooks/useOrderSetup';

// The providers this deployment supports, shown one row each (connected or not).
const SUPPORTED: OnlinePaymentProvider[] = ['PAYPAL', 'PAYCAT'];

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2500, position: 'bottom' });

function PaymentsSettingsPage() {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: providers, isLoading, isError, error } = usePaymentProviders();
  const setActivation = useSetProviderActivation();
  const errorMessages = useErrorT();

  const providerName: Record<OnlinePaymentProvider, string> = {
    PAYPAL: t.payments.providerPaypal,
    PAYCAT: t.payments.providerPaycat,
  };

  // Credentials are write-only (never returned), so there's nothing to "view".
  // Opening a provider is for reconnect (re-enter creds) / disconnect only.
  // The mode travels in the query so the form opens on the row that was tapped.
  const open = (provider: OnlinePaymentProvider, mode?: PaymentProviderMode) =>
    f7.views.current.router.navigate(
      `/settings/payments/connect/${provider}${mode ? `?mode=${mode}` : ''}`,
    );

  // Activate/deactivate a SPECIFIC mode row (only ever called for a mode that
  // has saved creds - the toggle isn't rendered otherwise). Providers run in
  // PARALLEL: activating one leaves the others on and the customer chooses at
  // checkout. Only the two MODE rows of the SAME provider are exclusive, and the
  // server enforces that; the list refetch then flips the sibling toggle off.

  // Any active row still on sandbox credentials → the red safety banner. Checked
  // across every provider: one live gateway does not make a second sandbox one
  // safe, since the customer can pick either.
  const hasActiveSandbox = providers?.some((p) => p.isActive && p.mode === 'SANDBOX') ?? false;

  const toggleActive = (
    provider: OnlinePaymentProvider,
    mode: PaymentProviderMode,
    isActive: boolean,
  ) => {
    // Is THIS provider currently taking real money? Switching it to sandbox is
    // the destructive move, regardless of what the other providers are doing.
    const liveActive =
      providers?.some((p) => p.provider === provider && p.isActive && p.mode === 'LIVE') ?? false;
    const persist = () =>
      setActivation.mutate(
        { provider, mode, isActive },
        {
          onSuccess: () =>
            toast(isActive ? t.payments.activatedToast : t.payments.deactivatedToast),
          onError: (e) => toast(resolveErrorText(e, errorMessages)),
        },
      );
    // Switching the storefront from a live provider to sandbox stops real
    // payments — confirm before flipping.
    if (isActive && mode === 'SANDBOX' && liveActive) {
      f7.dialog.confirm(t.payments.downgradeText, t.payments.downgradeTitle, persist);
    } else {
      persist();
    }
  };

  return (
    <Page name="payments-settings">
      <Navbar title={t.payments.title} backLink={settingsBackLink} />

      <BlockTitle>{t.payments.onlineTitle}</BlockTitle>
      <BlockFooter>{t.payments.intro}</BlockFooter>

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {isError && (
        <Block strong inset>
          <p>{resolveErrorText(error, errorMessages)}</p>
        </Block>
      )}

      {!isLoading && !isError && (
        <>
          {/* Loud safety net: a real shop must never silently take fake money.
              Shown whenever the ACTIVE credentials are the sandbox set. */}
          {hasActiveSandbox && (
            <Block strong inset style={{ background: 'var(--f7-color-red)', color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <i className="f7-icons" style={{ fontSize: 24 }}>
                  exclamationmark_triangle_fill
                </i>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>
                    {t.payments.sandboxBannerTitle}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.35 }}>
                    {t.payments.sandboxBannerBody}
                  </div>
                </div>
              </div>
            </Block>
          )}

          {/* One grouped card per provider: both mode rows (Test / Live) with
              their connection state and, when connected, an active toggle. A mode
              with no saved creds shows a Connect link instead - so a mode can
              never be activated from here before its keys exist. */}
          {SUPPORTED.map((prov) => {
            const rows = providers?.filter((p) => p.provider === prov) ?? [];
            const sandboxCfg = rows.find((r) => r.mode === 'SANDBOX') ?? null;
            const liveCfg = rows.find((r) => r.mode === 'LIVE') ?? null;
            const renderModeRow = (
              mode: PaymentProviderMode,
              cfg: PaymentProviderConfig | null,
            ) => (
              <ListItem
                key={mode}
                title={mode === 'LIVE' ? t.payments.modeLive : t.payments.modeTest}
                footer={
                  cfg
                    ? cfg.isActive
                      ? t.payments.stateActive
                      : t.payments.stateConnected
                    : t.payments.notConnected
                }
              >
                {/* Leading status icon, like the printer status list: the active
                    mode (the one taking money) gets the green check; every other
                    mode gets a muted minus. */}
                <i
                  slot="media"
                  className="f7-icons"
                  style={{
                    color: cfg?.isActive ? '#34c759' : '#8e8e93',
                    fontSize: 20,
                  }}
                >
                  {cfg?.isActive ? 'checkmark_alt_circle_fill' : 'minus_circle_fill'}
                </i>
                {cfg ? (
                  <ToggleSwitch
                    slot="after"
                    checked={cfg.isActive}
                    disabled={setActivation.isPending}
                    onChange={(v) => toggleActive(prov, mode, v)}
                  />
                ) : (
                  <F7Link slot="after" onClick={() => open(prov, mode)}>
                    {t.payments.connectShort}
                  </F7Link>
                )}
              </ListItem>
            );
            return (
              <div key={prov}>
                {/* Tap the name to connect / reconnect / disconnect (creds can't
                    be viewed). */}
                <BlockTitle>
                  <F7Link
                    onClick={() => open(prov, (rows.find((r) => r.isActive) ?? rows[0])?.mode)}
                  >
                    {providerName[prov]}
                  </F7Link>
                </BlockTitle>
                <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
                  {renderModeRow('SANDBOX', sandboxCfg)}
                  {renderModeRow('LIVE', liveCfg)}
                </List>
              </div>
            );
          })}
        </>
      )}

      {/* Per-order-type card on/off - only shows once an online provider is
          connected. Moved here from the order-setup tab (UI unchanged). */}
      <CardPaymentsSection />

      {/* Card-at-handoff: customer pays by card on the owner's portable POS at
          pickup/delivery. Needs no online provider (settled like cash), so it
          renders unconditionally - unlike the online-card section above. */}
      <CardOnHandoffSection />

      {/* ── Manual methods the owner defines (bank transfer / VietQR / e-wallet).
          Kept in the same tab as the online providers so the whole "how do
          customers pay" surface lives in one place, but under its own heading so
          it reads as distinct from the ready-made online providers above. ── */}
      <ManualMethodsBlock />

      {/* One short note per rail, shown to the guest beside that method at
          checkout. Every rail the guest can actually see lives in ONE list here -
          the alternative, a note field inside each section above, would scatter
          the same setting across four places and hide it inside a provider's
          credentials form. */}
      <CheckoutBadgesSection />

      {/* How long a customer has to transfer + upload proof before the order is
          auto-cancelled (OrderTypeConfig.expireUnpaidAfterMinutes). Governs both
          manual transfer and online-intent windows, so it sits under the payment
          methods it applies to. */}
      <PaymentWindowSection />
    </Page>
  );
}

// Owner-editable payment window: minutes an unpaid order stays PENDING for the
// customer to pay + upload proof before the stale-order sweep cancels it. One row
// per order type (PICKUP/DELIVERY), edited via a numeric prompt - mirrors the
// minimum-order pattern on the order-setup tab.
function PaymentWindowSection() {
  const t = useT();
  const pw = t.payments;
  const op = t.orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  // Fall back to the backend default (45) when the field is absent - e.g. an
  // older API build that predates this setting - so the UI never shows "undefined".
  const DEFAULT_EXPIRE_MIN = 45;

  const edit = (cfg: NonNullable<typeof configs>[number]) => {
    const orderType = cfg.orderType;
    const label = orderType === 'PICKUP' ? op.pickup : op.delivery;
    const current = cfg.expireUnpaidAfterMinutes ?? DEFAULT_EXPIRE_MIN;
    f7.dialog.prompt(
      pw.paymentWindowPrompt(label.toLowerCase()),
      pw.paymentWindowDialogTitle,
      (value) => {
        const mins = Math.round(Number(value));
        // Reject blanks, non-numbers, zero/negatives, and anything past 24 h.
        if (!Number.isInteger(mins) || mins < 1 || mins > 1440) {
          toast(pw.paymentWindowInvalid);
          return;
        }
        update.mutate(
          { orderType, data: { expireUnpaidAfterMinutes: mins } },
          { onSuccess: () => toast(pw.paymentWindowSet(label, mins)) },
        );
      },
      undefined,
      String(current),
    );
  };

  if (isLoading || !configs?.length) return null;

  return (
    <>
      <BlockTitle>{pw.paymentWindowTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{pw.paymentWindowFooter}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {configs.map((cfg) => (
          <ListItem
            key={cfg.orderType}
            link="#"
            onClick={() => edit(cfg)}
            title={cfg.orderType === 'PICKUP' ? op.pickup : op.delivery}
            subtitle={pw.paymentWindowValue(cfg.expireUnpaidAfterMinutes ?? DEFAULT_EXPIRE_MIN)}
          >
            <i slot="media" className="f7-icons">
              timer
            </i>
          </ListItem>
        ))}
      </List>
    </>
  );
}

// Per-order-type "accept card online" toggle. Only appears once an online
// provider is connected (onlineProviderConfigured). Moved verbatim from the
// order-setup tab so the card enable/disable lives next to the provider it
// depends on - UI and copy (t.orderSetup.page) unchanged.
function CardPaymentsSection() {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toggleCard = (cfg: NonNullable<typeof configs>[number], next: boolean) => {
    const label = cfg.orderType === 'PICKUP' ? p.pickup : p.delivery;
    update.mutate(
      { orderType: cfg.orderType, data: { onlineEnabled: next } },
      { onSuccess: () => toast(next ? p.cardOnToast(label) : p.cardOffToast(label)) },
    );
  };

  const cardConfigs = configs?.filter((c) => c.onlineProviderConfigured) ?? [];
  if (isLoading || cardConfigs.length === 0) return null;

  return (
    <>
      <BlockTitle>{p.cardTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.cardFooter}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {cardConfigs.map((cfg) => {
          const isPickup = cfg.orderType === 'PICKUP';
          return (
            <ListItem
              key={cfg.orderType}
              title={isPickup ? p.pickup : p.delivery}
              subtitle={cfg.onlineEnabled ? p.acceptingCard : p.cardOff}
            >
              <i slot="media" className="f7-icons">
                {isPickup ? 'bag' : 'car_fill'}
              </i>
              <ToggleSwitch
                slot="after"
                checked={cfg.onlineEnabled}
                disabled={update.isPending}
                onChange={(v) => toggleCard(cfg, v)}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

// Per-order-type "card at handoff" toggle: the customer pays by card on the
// owner's portable POS terminal at pickup/delivery. It needs no online provider
// (the owner collects and settles it in person, like cash), so - unlike
// CardPaymentsSection - this renders for every order type unconditionally.
function CardOnHandoffSection() {
  const p = useT().orderSetup.page;
  const { data: configs, isLoading } = useOrderTypeConfig();
  const update = useUpdateOrderTypeConfig();

  const toggle = (cfg: NonNullable<typeof configs>[number], next: boolean) => {
    const label = cfg.orderType === 'PICKUP' ? p.pickup : p.delivery;
    update.mutate(
      { orderType: cfg.orderType, data: { cardOnHandoffEnabled: next } },
      {
        onSuccess: () =>
          toast(next ? p.cardOnHandoffOnToast(label) : p.cardOnHandoffOffToast(label)),
      },
    );
  };

  if (isLoading || !configs?.length) return null;

  return (
    <>
      {/* Title + intro caption as an iOS grouped-list header (matches the online-
          providers section at the top): the description is the muted BlockFooter
          directly under the title - tightened with a scoped negative top margin -
          not a paragraph trailing the list. */}
      <BlockTitle>{p.cardOnHandoffTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{p.cardOnHandoffHelp}</BlockFooter>
      <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {configs.map((cfg) => {
          const isPickup = cfg.orderType === 'PICKUP';
          return (
            <ListItem
              key={cfg.orderType}
              title={isPickup ? p.pickup : p.delivery}
              subtitle={cfg.cardOnHandoffEnabled ? p.acceptingCardOnHandoff : p.cardOnHandoffOff}
            >
              <i slot="media" className="f7-icons">
                {isPickup ? 'bag' : 'car_fill'}
              </i>
              <ToggleSwitch
                slot="after"
                checked={cfg.cardOnHandoffEnabled}
                disabled={update.isPending}
                onChange={(v) => toggle(cfg, v)}
              />
            </ListItem>
          );
        })}
      </List>
    </>
  );
}

// Owner-defined offline payment methods: the owner enters the details (bank
// account, QR, instructions) and the customer pays offline then uploads proof -
// unlike the online providers above, which capture card/wallet automatically.
// Reuses the manual-method hooks + copy that used to live in the order-setup tab.
function ManualMethodsBlock() {
  const t = useT();
  const p = t.orderSetup.page;
  const { data: methods, isLoading } = useManualPaymentMethods();
  const deleteMethod = useDeleteManualPaymentMethod();

  const nav = (path: string) => f7.views.current.router.navigate(path);

  const handleDelete = (methodId: string, name: string) => {
    f7.dialog.confirm(p.deleteMethodConfirm(name), p.deleteMethodTitle, () => {
      deleteMethod.mutate(methodId, {
        onSuccess: () => toast(p.methodDeleted),
      });
    });
  };

  return (
    <>
      <BlockTitle>{t.payments.manualTitle}</BlockTitle>
      <BlockFooter>{t.payments.manualIntro}</BlockFooter>

      {isLoading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : (
        // One grouped iOS list: the methods - or a muted empty-state caption when
        // there are none - and the "add" action share a SINGLE inset card, rather
        // than a floating message block above a separate add block.
        <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {!methods?.length ? (
            <ListItem>
              <div
                slot="title"
                style={{
                  whiteSpace: 'normal',
                  color: 'var(--f7-list-item-subtitle-text-color)',
                }}
              >
                {p.noMethods}
              </div>
            </ListItem>
          ) : (
            methods.map((m) => (
              <ListItem
                key={m.id}
                link="#"
                onClick={() => nav(`/settings/payments/methods/${m.id}`)}
                title={m.label}
                subtitle={m.instructions.slice(0, 80)}
              >
                <i slot="media" className="f7-icons">
                  building_2_fill
                </i>
                <span
                  slot="after"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12,
                    color: m.isActive
                      ? 'var(--f7-theme-color)'
                      : 'var(--f7-list-item-subtitle-text-color)',
                    fontWeight: 600,
                  }}
                >
                  {m.isActive ? p.active : p.inactive}
                  {/* Plain <i>, NOT <F7Link> - the row is already an <a> (link="#"),
                      and an anchor can't nest another anchor (hydration error).
                      preventDefault stops the row-link nav; stopPropagation stops
                      the ListItem onClick. */}
                  <i
                    className="f7-icons"
                    role="button"
                    aria-label={p.deleteMethodTitle}
                    style={{ color: 'var(--f7-color-red)', cursor: 'pointer', fontSize: 22 }}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDelete(m.id, m.label);
                    }}
                  >
                    trash
                  </i>
                </span>
              </ListItem>
            ))
          )}
          {/* Always the last row of the same card - merges with the list above. */}
          <ListItem>
            <F7Link
              slot="title"
              onClick={() => nav('/settings/payments/methods/new')}
              style={{ color: 'var(--f7-theme-color)' }}
            >
              {p.addMethod}
            </F7Link>
          </ListItem>
        </List>
      )}
    </>
  );
}

// Owner-written checkout badges ("Recommended"), one optional note per payment
// rail. Only rails the guest can actually reach are listed: cash is always on,
// card-at-handoff appears once it is enabled for an order type, a gateway once
// its keys are saved, and every manual method the owner has defined.
function CheckoutBadgesSection() {
  const t = useT();
  const pay = t.payments;
  const errorMessages = useErrorT();
  const { data: notes, isLoading: notesLoading } = usePaymentMethodNotes();
  const { data: providers } = usePaymentProviders();
  const { data: manualMethods } = useManualPaymentMethods();
  const { data: configs } = useOrderTypeConfig();
  const setNote = useSetPaymentMethodNote();

  const noteFor = (methodId: string) => notes?.find((n) => n.methodId === methodId)?.note ?? '';

  const rails: { methodId: string; label: string; icon: string }[] = [
    { methodId: CASH_METHOD_ID, label: pay.badgeMethodCash, icon: 'money_dollar_circle' },
  ];
  if (configs?.some((c) => c.cardOnHandoffEnabled)) {
    rails.push({
      methodId: CARD_ON_HANDOFF_METHOD_ID,
      label: pay.badgeMethodCardOnHandoff,
      icon: 'creditcard',
    });
  }
  // Dedupe: a provider holds up to two rows (Test + Live) but is ONE rail to the
  // guest, so its badge is keyed by provider, not by credential row.
  for (const provider of SUPPORTED) {
    if (!providers?.some((row) => row.provider === provider)) continue;
    const methodId = ONLINE_PROVIDER_METHOD_ID_BY_ENUM[provider];
    rails.push({
      methodId,
      label: ONLINE_PROVIDER_IDENTITY[methodId].shortName,
      icon: 'creditcard_fill',
    });
  }
  for (const m of manualMethods ?? []) {
    rails.push({ methodId: m.id, label: m.label, icon: 'building_2_fill' });
  }

  const edit = (methodId: string, label: string) => {
    f7.dialog.prompt(
      pay.badgePrompt(label),
      pay.badgeDialogTitle,
      (value: string) => {
        const note = value.trim();
        if (note.length > PAYMENT_METHOD_NOTE_MAX_LENGTH) {
          toast(pay.badgeTooLong(PAYMENT_METHOD_NOTE_MAX_LENGTH));
          return;
        }
        setNote.mutate(
          { methodId, note },
          {
            onSuccess: () => toast(note ? pay.badgeSetToast(label) : pay.badgeClearedToast(label)),
            onError: (e) => toast(resolveErrorText(e, errorMessages)),
          },
        );
      },
      undefined,
      noteFor(methodId),
    );
  };

  return (
    <>
      <BlockTitle>{pay.badgesTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{pay.badgesFooter}</BlockFooter>
      {notesLoading ? (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      ) : (
        <List mediaList strongIos strongMd insetIos insetMd dividersIos dividersMd>
          {rails.map((rail) => (
            <ListItem
              key={rail.methodId}
              link="#"
              onClick={() => edit(rail.methodId, rail.label)}
              title={rail.label}
              subtitle={noteFor(rail.methodId) || pay.badgeNone}
            >
              <i slot="media" className="f7-icons">
                {rail.icon}
              </i>
            </ListItem>
          ))}
        </List>
      )}
    </>
  );
}

export default PaymentsSettingsPage;
